/**
 * WizardModal — Radix Dialog wrapper around <SetupWizard/>.
 *
 * The wizard owns the entire pre-debate configuration flow and lives inside
 * a glassmorphic modal so the main canvas (roundtable + transcript + verdict)
 * stays uncluttered. The modal closes automatically when:
 *   - the user finishes the wizard and starts the debate (handled inside
 *     SetupWizard.onStart → closeWizard),
 *   - the debate transitions away from "draft" by any means.
 */
import { useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { motion, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";

import { useDebateStore } from "../../store/debateStore";
import { selectSessionLifecycle } from "../../store/selectors";
import { Button } from "../common/Button";
import { cn } from "../common/cn";
import { SetupWizard } from "./SetupWizard";

export function WizardModal() {
  const open = useDebateStore((s) => s.wizardOpen);
  const openWizard = useDebateStore((s) => s.openWizard);
  const closeWizard = useDebateStore((s) => s.closeWizard);
  const lifecycle = useDebateStore(selectSessionLifecycle);
  const personasReady = useDebateStore((s) => s.wizard.personas.length > 0);
  const reduceMotion = useReducedMotion();

  // Sihirbaz bir işin ORTASINDA mı? Konsey üretimi ~2 dakikaya kadar
  // sürebiliyor ve o sırada ekranda yalnızca bir bekleme göstergesi var.
  // Radix'in varsayılanı boşluğa tıklamayı "kapat" saydığı için kullanıcı
  // beklerken farkında olmadan pencereyi kapatıyor, iş arka planda sessizce
  // bitiyor ve geri döndüğünde ekranı değişmiş buluyor — operatörün
  // "dışarı tıklayınca süreç ilerliyor" dediği davranış tam olarak budur.
  // İş sürerken kazara kapanmayı engelliyoruz; kapatma niyeti varsa X
  // düğmesi hâlâ çalışıyor.
  const busy = useDebateStore(
    (s) =>
      s.wizard.generationStatus === "generating" ||
      s.wizard.quality.status === "assessing" ||
      s.wizard.quality.status === "refining" ||
      s.wizard.candidates.status === "loading",
  );

  const blockAccidentalClose = (e: Event) => {
    if (!busy) return;
    e.preventDefault();
    toast.message("Sihirbaz çalışıyor", {
      description:
        "Konsey hazırlanıyor. Kapatmak istersen sağ üstteki kapatma " +
        "düğmesini kullan — ilerleme korunur.",
    });
  };

  // First-load auto-open: if the user lands on a draft session with no personas
  // yet, surface the wizard immediately so the page is never silent.
  useEffect(() => {
    if (lifecycle.status === "draft" && !personasReady) {
      openWizard();
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-close once the debate moves away from draft (covers wizard.onStart →
  // session.start → status transitions) AND when the session is reset back to
  // draft, leave it to the explicit openWizard() call.
  useEffect(() => {
    if (open && lifecycle.status !== "draft") closeWizard();
  }, [open, lifecycle.status, closeWizard]);

  // Sihirbaz kapalıyken konsey üretimi tamamlanırsa kullanıcı bunu göremez:
  // pencere kapanmış, iş arka planda sürmüştür ve biter. Sessiz kalmak, geri
  // dönüldüğünde "ben bunu istemedim" hissi yaratıyor. Tamamlanmayı bildirip
  // tek tıkla geri dönüş sunuyoruz.
  useEffect(() => {
    if (open || !personasReady) return;
    if (lifecycle.status !== "draft") return;
    toast.success("Konsey hazır", {
      id: "wizard-council-ready",
      description: "Sihirbazı açıp gözden geçirebilirsin.",
      action: { label: "Aç", onClick: () => openWizard() },
    });
  }, [open, personasReady, lifecycle.status, openWizard]);

  return (
    <Dialog.Root open={open} onOpenChange={(o) => (o ? openWizard() : closeWizard())}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-40 bg-[hsl(var(--bg-page)/0.72)] backdrop-blur-md",
            reduceMotion
              ? ""
              : "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
          )}
        />
        <Dialog.Content
          aria-describedby={undefined}
          onInteractOutside={blockAccidentalClose}
          onEscapeKeyDown={blockAccidentalClose}
          className={cn(
            "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
            "w-[min(96vw,1100px)] max-h-[92vh] overflow-hidden",
            "outline-none",
          )}
        >
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 12, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.99 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="relative max-h-[92vh] overflow-y-auto rounded-3xl"
          >
            {/* Floating close button (Radix's built-in close anchored over the
                wizard card so it always sits on the modal chrome). */}
            <Dialog.Close asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Sihirbazı kapat"
                className="absolute right-4 top-4 z-10 rounded-full bg-surface-2/70 backdrop-blur"
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </Dialog.Close>

            <Dialog.Title className="sr-only">Konseyi Yapılandır</Dialog.Title>
            <SetupWizard />
          </motion.div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
